import React, { useCallback, useEffect, useRef, useState } from 'react'
import { t } from '../../i18n.js'
import { formatPercent, formatWhole } from '../valuation_summary/format.js'
import {
  canAdjustDiscountRate,
  canAdjustExitCapRate,
  nextDiscountRate,
  nextExitCapRate,
  readExpansion,
  writeExpansion
} from '../valuation_summary/panel.js'
import { generateRequestId } from '../utils/requestId.js'
import { showError } from '../utils/notifications.js'
import useValuationPulse from '../hooks/useValuationPulse.js'

const PANEL_STEPS = [-5, 5]

// EVA-296: the floating Value Conclusion panel, mounted from the shared
// valuation header on every sub-tab. Displayed values are read-only and come
// from valuation_summary.json — the panel performs no valuation maths. The
// only mutating controls are the ±5 bps groups, which PATCH the same
// authoritative endpoints the Discounting and Exit Price tabs autosave to
// (assumption-only patches: no schedule key, so no schedule is touched).
//
// Sub-tabs are separate Turbo visits, so the component re-mounts on every
// navigation; the disclosure state survives in sessionStorage (panel.js).
export default function FloatingValueConclusion({
  valuationId, stateUrl, discountingUpdateUrl, exitUpdateUrl, csrfToken, readOnly, translations
}) {
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const fetchRef = useRef(null)
  // Monotonic sequence guard, same shape as CashFlowReport: a slow mount
  // fetch must not overwrite the fresher realtime refetch that overtook it.
  const requestSeqRef = useRef(0)
  const appliedSeqRef = useRef(0)
  // Mirror of `data` for the adjust handlers, which re-read the rate after an
  // awaited refetch — state would hand them the render they closed over.
  const dataRef = useRef(null)
  const [expanded, setExpanded] = useState(() => readExpansion(window.sessionStorage, valuationId))
  const [data, setData] = useState(null)
  const [saving, setSaving] = useState(false)

  const label = useCallback((key, vars) => t(key, translations, vars || {}), [translations])

  // Answers with the freshest payload on success and null on failure, so the
  // adjust handlers can tell a refused refetch from a fresh one instead of
  // stepping off whatever dataRef still holds.
  const fetchSummary = useCallback(async () => {
    const seq = (requestSeqRef.current += 1)
    try {
      const res = await fetch(stateUrl, { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) return null
      // Superseded by a later response: dataRef already holds something
      // FRESHER, which is exactly what the caller should use.
      if (seq < appliedSeqRef.current) return dataRef.current
      appliedSeqRef.current = seq
      dataRef.current = json.data
      setData(json.data)
      return json.data
    } catch {
      // A failed background read leaves the previous values (or dashes) in
      // place; the panel is a reference, not a form with errors to report.
      return null
    }
  }, [stateUrl])

  useEffect(() => { fetchRef.current = fetchSummary }, [fetchSummary])
  useEffect(() => { fetchSummary() }, [fetchSummary])

  // EVA-300: the panel's figures are stale from the pulse until the refetch it
  // debounces has applied, and used to say nothing about it — the number simply
  // sat there looking final for ~1.5s. `recalculating` dims the values and puts
  // a spinner beside the label for exactly that window.
  //
  // This also fires for the panel's OWN ±5 bps step, whose `adjust` has already
  // refreshed these numbers — so they dim briefly having just been correct.
  // Kept on purpose: a pulse carries no promise of being ours (another member
  // editing the same valuation lands in the same window), the Summary tab
  // behind the panel genuinely IS stale, and suppressing the echo would mean
  // tracking our own request ids — the machinery frontend.md warns is easy to
  // get subtly wrong, bought here for a cosmetic gain.
  const recalculating = useValuationPulse(valuationId, fetchSummary)

  const toggle = useCallback(() => {
    setExpanded((previous) => {
      const next = !previous
      writeExpansion(window.sessionStorage, valuationId, next)
      return next
    })
  }, [valuationId])

  const collapse = useCallback(({ restoreFocus = false } = {}) => {
    setExpanded(false)
    writeExpansion(window.sessionStorage, valuationId, false)
    if (restoreFocus) triggerRef.current?.focus()
  }, [valuationId])

  // Outside pointer and Escape dismissal, registered only while expanded.
  // Escape returns focus to the trigger; an outside click lets focus follow
  // the clicked target naturally.
  useEffect(() => {
    if (!expanded) return undefined

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) collapse()
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') collapse({ restoreFocus: true })
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [expanded, collapse])

  const patchRate = useCallback(async (url, body) => {
    try {
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ ...body, request_id: generateRequestId() })
      })
      const json = await res.json().catch(() => null)
      if (res.ok && json?.success !== false) {
        await fetchRef.current?.()
      } else {
        // A refused step (out-of-range rate, access revoked mid-session) must
        // not read as a dead button.
        showError(label('adjust_failed'))
      }
    } catch {
      showError(label('adjust_failed'))
    }
  }, [csrfToken, label])

  const kpis = data?.kpis || {}
  const headline = data?.headline || {}
  const discountRate = kpis.discount_rate_percent ?? null
  const exitCapRate = kpis.exit_cap_rate_percent ?? null

  // Read–modify–write against the AUTHORITATIVE value: another tab may have
  // just saved a different rate, and the panel's copy is up to 1.5s stale —
  // so re-read first, then compute the step off what actually persisted.
  const adjust = useCallback(async (bps, computeNext, readRate, url, field) => {
    setSaving(true)
    try {
      const fresh = await fetchRef.current?.()
      if (!fresh) {
        showError(label('adjust_failed'))
        return
      }
      const next = computeNext(readRate(fresh), bps)
      if (next === null) return
      await patchRate(url, { [field]: next })
    } finally {
      setSaving(false)
    }
  }, [patchRate, label])

  const adjustDiscount = useCallback((bps) => {
    adjust(
      bps,
      nextDiscountRate,
      (payload) => payload?.kpis?.discount_rate_percent ?? null,
      discountingUpdateUrl,
      'discount_rate_percent'
    )
  }, [adjust, discountingUpdateUrl])

  const adjustExit = useCallback((bps) => {
    adjust(
      bps,
      nextExitCapRate,
      (payload) => payload?.kpis?.exit_cap_rate_percent ?? null,
      exitUpdateUrl,
      'exit_rate_percent'
    )
  }, [adjust, exitUpdateUrl])

  return (
    // aria-busy rather than a live region: eleven controllers broadcast and a
    // grid fires a pulse per edited row, so announcing every one would talk
    // over the user. Known limitation, accepted rather than solved — without a
    // live region most screen readers will not volunteer aria-busy, so this
    // state is effectively sighted-only. The alternative announces on every
    // keystroke, which is worse.
    <aside ref={rootRef}
           className={`value-conclusion${expanded ? ' value-conclusion--expanded' : ''}${recalculating ? ' value-conclusion--recalculating' : ''}`}
           aria-busy={recalculating}
           aria-label={label('aria_label')}>
      <button
        ref={triggerRef}
        type="button"
        className="value-conclusion__trigger"
        aria-expanded={expanded}
        aria-controls="valuation-conclusion-details"
        onClick={toggle}
      >
        <span className="value-conclusion__label">
          {label('title')}
          {/* aria-hidden because the spinner is decoration: it repeats what the
              dimmed figures already say, and exposing it would announce on
              every pulse. Note this is NOT what closes the gap described
              above — if that is ever closed it will be with a live region,
              not by unhiding this. */}
          {recalculating && (
            <span className="recalculating-spinner spinner-border spinner-border-sm" aria-hidden="true" />
          )}
        </span>
        <strong className="value-conclusion__headline" data-testid="vc-headline">
          {formatWhole(headline.rounded_dcf_net_value)}
        </strong>
        <span className={`value-conclusion__chevron${expanded ? ' value-conclusion__chevron--open' : ''}`} aria-hidden="true">
          ▴
        </span>
      </button>

      <div id="valuation-conclusion-details" className="value-conclusion__details" hidden={!expanded}>
        <div className="value-conclusion__row">
          <span className="value-conclusion__row-label">{label('dcf_net_value')}</span>
          <span className="value-conclusion__row-value">{formatWhole(headline.dcf_net_value)}</span>
        </div>
        <div className="value-conclusion__row">
          <span className="value-conclusion__row-label">{label('niy')}</span>
          <span className="value-conclusion__row-value">{formatPercent(kpis.niy_percent)}</span>
        </div>
        <RateRow
          label={label('discount_rate')}
          value={formatPercent(discountRate)}
          readOnly={readOnly}
          saving={saving}
          canAdjust={(bps) => canAdjustDiscountRate(discountRate, bps)}
          onAdjust={adjustDiscount}
          minusLabel={label('quick_adjust.minus_5')}
          plusLabel={label('quick_adjust.plus_5')}
          ariaLabel={label('quick_adjust.discount_aria')}
        />
        <RateRow
          label={label('exit_cap_rate')}
          value={formatPercent(exitCapRate)}
          readOnly={readOnly}
          saving={saving}
          canAdjust={(bps) => canAdjustExitCapRate(exitCapRate, bps)}
          onAdjust={adjustExit}
          minusLabel={label('quick_adjust.minus_5')}
          plusLabel={label('quick_adjust.plus_5')}
          ariaLabel={label('quick_adjust.exit_aria')}
        />
      </div>
    </aside>
  )
}

function RateRow({ label, value, readOnly, saving, canAdjust, onAdjust, minusLabel, plusLabel, ariaLabel }) {
  return (
    <div className="value-conclusion__row value-conclusion__row--rate">
      <span className="value-conclusion__row-label">{label}</span>
      <span className="value-conclusion__rate-group">
        {!readOnly && (
          <span className="btn-group btn-group-sm" role="group" aria-label={ariaLabel}>
            {PANEL_STEPS.map((bps) => (
              <button
                key={bps}
                type="button"
                className="btn btn-outline-secondary"
                disabled={saving || !canAdjust(bps)}
                onClick={(event) => { event.stopPropagation(); onAdjust(bps) }}
              >
                {bps < 0 ? minusLabel : plusLabel}
              </button>
            ))}
          </span>
        )}
        <span className="value-conclusion__row-value">{value}</span>
      </span>
    </div>
  )
}
